import { useState } from 'react';
import { Input, Modal, Tooltip, message } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import { isSupportLead } from '../../../../helpers/genericHelper';
import API from '../../../../services/API';

// Soft-deleted and released numbers carry this marker; nothing left to release.
export const RELEASED_MARKER = '-XX';

/**
 * Renders as the phone Input's `suffix`: a clickable icon that frees the number for another
 * account. Stays clickable while the input itself is disabled, so it doesn't depend on the
 * "Phone Number" checkbox that gates editing.
 */
export default function ReleasePhoneNumber({ userId, phoneNumber, reloadData }) {
  const [modalVisible, setModalVisible] = useState(false);
  const [reason, setReason] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!phoneNumber || phoneNumber.includes(RELEASED_MARKER)) {
    return null;
  }

  // Lower roles see the control but can't use it, so they know it exists and who to escalate to.
  if (!isSupportLead()) {
    return (
      <Tooltip title="Releasing a phone number requires a Support Lead or Manager">
        <CloseCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', cursor: 'not-allowed', pointerEvents: 'auto' }} />
      </Tooltip>
    );
  }

  const closeModal = () => {
    setReason(null);
    setModalVisible(false);
  };

  const releaseConfirmed = async () => {
    setLoading(true);
    try {
      await API.releasePhoneNumber(userId, reason);
      message.success('Phone number released');
      closeModal();
      reloadData();
    } catch (error) {
      message.error(error?.response?.data?.error_message || 'Unable to release phone number');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Release this phone number so another customer can use it">
        <CloseCircleOutlined
          onClick={(e) => { e.stopPropagation(); setModalVisible(true); }}
          style={{ color: '#ff4d4f', cursor: 'pointer', pointerEvents: 'auto' }}
        />
      </Tooltip>
      <Modal
        title="Release phone number"
        centered
        destroyOnClose={true}
        visible={modalVisible}
        onOk={releaseConfirmed}
        onCancel={closeModal}
        okText="Release"
        okButtonProps={{ danger: true, disabled: !reason?.trim(), loading }}
        className="confirm-modal"
      >
        <p>
          This frees <strong>{phoneNumber}</strong> so a different customer can sign up with it.
          The account keeps everything else but will no longer have a phone number on file.
        </p>
        <Input
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (ticket number, who verified ownership)..."
          value={reason}
        />
      </Modal>
    </>
  );
}
